import { useEffect, useState } from 'react';
import { X } from '@phosphor-icons/react';
import { useSettings } from '@/context/SettingsContext';

/**
 * Site-wide running banner controlled fully from Admin -> Appearance/Running Banner.
 * Two styles:
 *   - marquee: continuously scrolling horizontal ticker
 *   - static:  fixed bar with optional dismiss button (persisted per session)
 */
export default function RunningBanner() {
  const { settings } = useSettings();
  const rb = settings?.running_banner || {};
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem('mzk_rb_dismissed') === (rb.text || ''));
  }, [rb.text]);

  if (!rb.enabled || !rb.text || dismissed) return null;

  const style = rb.style === 'static' ? 'static' : 'marquee';
  const bg = rb.bg_color || '#0F8A43';
  const fg = rb.text_color || '#FFFFFF';
  const speed = Math.max(5, Number(rb.speed) || 30);
  const height = 'var(--app-banner-h, 40px)';

  if (style === 'static') {
    return (
      <div
        data-testid="running-banner-static"
        className="w-full flex items-center justify-center gap-3 text-sm px-4"
        style={{ background: bg, color: fg, minHeight: height }}
      >
        <span className="text-center leading-snug">{rb.text}</span>
        {rb.dismissible !== false && (
          <button
            data-testid="running-banner-dismiss"
            aria-label="Dismiss banner"
            onClick={() => { sessionStorage.setItem('mzk_rb_dismissed', rb.text); setDismissed(true); }}
            className="opacity-80 hover:opacity-100"
            style={{ color: fg }}
          >
            <X size={16} weight="bold" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="running-banner-marquee"
      className="w-full overflow-hidden text-sm relative"
      style={{ background: bg, color: fg, minHeight: height }}
    >
      <div
        className="whitespace-nowrap py-2 flex items-center"
        style={{ animation: `mzkMarquee ${speed}s linear infinite` }}
      >
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="px-8 shrink-0 tracking-wide">{rb.text}</span>
        ))}
      </div>
      <style>{`
        @keyframes mzkMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>
    </div>
  );
}
